/**
 * Chat k hotové valuaci.
 *
 * Odpovídá jen z faktů této valuace, které si server sám přepočítá
 * (core/valuace_chat.py). Co ve faktech není, daně, právo nebo jednání
 * s kupcem, předá živému poradci M&Ai: rozhovor se uloží jako konzultace
 * a poradce na něj odpoví v sekci Konzultace.
 */
import React, { useEffect, useRef, useState } from 'react';
import { authHeaders } from '../lib/apiFetch';
import { buildValuationRequest } from '../api/valuationCalculator';
import { podkladyNavrhu } from '../api/rozbor';

const API_BASE = process.env.REACT_APP_API_BASE || '';

const NAVRHY = [
  'Proč vyšla hodnota právě takhle?',
  'Co hodnotu firmy nejvíc snižuje?',
  'Čím můžu hodnotu zvýšit?',
];

async function posli(cesta, telo) {
  const res = await fetch(`${API_BASE}${cesta}`, {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(telo),
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `Odpověď se nepodařilo získat (${res.status})`);
  }
  return res.json();
}

function souhrnAuditu(a) {
  if (!a) return null;
  return {
    lost_profit_total: a.lost_profit_total,
    lost_profit_pct_of_revenue: a.lost_profit_pct_of_revenue,
    priority_actions: (a.priority_actions || []).slice(0, 6)
      .map(x => ({ action: x.action, expected_impact: x.expected_impact })),
  };
}

export function ValuaceChat({ wizardState }) {
  const [zpravy, setZpravy] = useState([]);
  const [text, setText] = useState('');
  const [ceka, setCeka] = useState(false);
  const [chyba, setChyba] = useState(null);
  const [nabidnoutPoradce, setNabidnoutPoradce] = useState(false);
  const [predano, setPredano] = useState(false);
  const konec = useRef(null);

  useEffect(() => {
    if (konec.current && zpravy.length) konec.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [zpravy, ceka]);

  const firma = (wizardState.company && wizardState.company.company_name) || '';

  const zeptej = async (otazka) => {
    const q = (otazka || '').trim();
    if (!q || ceka) return;
    const historie = zpravy.map(z => ({ role: z.role, text: z.text }));
    setZpravy(prev => [...prev, { role: 'klient', text: q }]);
    setText(''); setCeka(true); setChyba(null);
    try {
      const { vykaz, odpovedi } = podkladyNavrhu(wizardState);
      const d = await posli('/api/valuace/chat', {
        otazka: q,
        historie,
        firma,
        valuation_id: wizardState.savedValuationId || null,
        vykaz,
        odpovedi,
        typ_podnikani: wizardState.typ_podnikani || null,
        valuation_request: buildValuationRequest(wizardState),
        audit: souhrnAuditu(wizardState.auditResult),
      });
      setZpravy(prev => [...prev, { role: 'asistent', text: d.odpoved, poradce: d.poradce }]);
      if (d.poradce) setNabidnoutPoradce(true);
    } catch (e) {
      setChyba(e.message);
    } finally {
      setCeka(false);
    }
  };

  const predej = async () => {
    setCeka(true); setChyba(null);
    try {
      await posli('/api/valuace/chat/predat', {
        historie: zpravy.map(z => ({ role: z.role, text: z.text })),
        firma,
        valuation_id: wizardState.savedValuationId || null,
      });
      setPredano(true);
    } catch (e) {
      setChyba(e.message);
    } finally {
      setCeka(false);
    }
  };

  return (
    <div className="edit-form" style={{ marginBottom: '1.5rem' }}>
      <h3 style={{
        fontFamily: "'Source Serif 4', Georgia, serif",
        fontWeight: 600, fontSize: '1.15rem', margin: '0 0 0.35rem',
      }}>
        Zeptejte se na své ocenění
      </h3>
      <p style={{ margin: '0 0 1rem', fontSize: '0.87rem', color: 'var(--text-dim)' }}>
        Asistent odpovídá jen z čísel a závěrů této valuace. Na otázky, které z nich vyčíst
        nejde, například daně, smlouvy nebo jednání s kupcem, vás spojí s poradcem M&amp;Ai.
      </p>

      {zpravy.length === 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
          {NAVRHY.map(n => (
            <button key={n} type="button" onClick={() => zeptej(n)} disabled={ceka}
              style={{
                padding: '0.4rem 0.8rem', borderRadius: 999, cursor: 'pointer',
                border: '1px solid var(--purple-mid)', background: 'var(--purple-deep)',
                color: 'var(--star-white)', fontSize: '0.82rem',
              }}>
              {n}
            </button>
          ))}
        </div>
      )}

      {zpravy.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '0.9rem', maxHeight: '26rem', overflowY: 'auto' }}>
          {zpravy.map((z, i) => (
            <div key={i} style={{
              alignSelf: z.role === 'klient' ? 'flex-end' : 'flex-start',
              maxWidth: '85%', padding: '0.6rem 0.85rem', borderRadius: 10,
              background: z.role === 'klient' ? 'var(--purple-glow)' : 'var(--purple-deep)',
              color: z.role === 'klient' ? 'white' : 'var(--star-white)',
              border: z.role === 'klient' ? 'none' : '1px solid var(--purple-mid)',
              fontSize: '0.88rem', lineHeight: 1.55, whiteSpace: 'pre-wrap',
            }}>
              {z.text}
            </div>
          ))}
          {ceka && <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>Píšu odpověď…</div>}
          <div ref={konec} />
        </div>
      )}

      {predano ? (
        <div style={{ padding: '0.7rem 0.9rem', borderRadius: 8, background: 'rgba(47,143,91,0.08)', border: '1px solid rgba(47,143,91,0.45)', fontSize: '0.87rem' }}>
          Rozhovor jsme předali poradci M&amp;Ai. Odpoví vám v sekci Konzultace.
        </div>
      ) : (
        <>
          <form onSubmit={e => { e.preventDefault(); zeptej(text); }} style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text" value={text} onChange={e => setText(e.target.value)} maxLength={800}
              placeholder="Napište otázku k ocenění nebo auditu"
              style={{ flex: 1 }} disabled={ceka}
            />
            <button type="submit" className="btn-primary" disabled={ceka || !text.trim()}>Zeptat se</button>
          </form>
          {(nabidnoutPoradce || zpravy.length >= 4) && (
            <button type="button" onClick={predej} disabled={ceka} className={nabidnoutPoradce ? 'btn-primary' : 'btn-secondary'}
              style={{ marginTop: '0.7rem', fontSize: '0.85rem' }}>
              Spojit s poradcem M&amp;Ai
            </button>
          )}
        </>
      )}
      {chyba && <div className="error-message" style={{ marginTop: '0.7rem' }}>{chyba}</div>}
    </div>
  );
}

export default ValuaceChat;
